import React, { useRef, useEffect, useState } from "react";
import AdditionalSections from "../components/WheatBagSections/AdditionalSections";
import WheatBagIntro from "../components/WheatBagSections/WheatBagIntro";
import { useTranslation } from "react-i18next";
import WheatBagFaqs from "../components/WheatBagSections/WheatBagFaqs";
import Product from "../components/Products";

function WheatBag() {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);

  const scrollToShopCollection = () => {
    const section = document.getElementById("wheatbag-shop-collection");
    if (!section) return;

    const topOffset = 96;
    const targetTop =
      section.getBoundingClientRect().top + window.pageYOffset - topOffset;

    window.scrollTo({ top: targetTop, behavior: "smooth" });
  };

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            videoElement.play();
          } else {
            videoElement.pause();
          }
        });
      },
      {
        threshold: 0.5,
      },
    );

    observer.observe(videoElement);
    return () => observer.disconnect();
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.msRequestFullscreen) {
        videoRef.current.msRequestFullscreen();
      }
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <div className="flex flex-wrap h-auto md:h-[46rem] canvas-background justify-center border-b border-[#c48173]">
        {/* Left Column */}
        <div className="order-2 md:order-none canvas-background w-full md:w-[35%] bg-[#f3d4de] p-[4.5rem] md:p-8 flex flex-col justify-center items-center text-center md:px-20 md:pt-20 md:pb-[4rem]">
          <div className="lg:mt-10 md:mt-[2rem] text-center w-full">
            <p className="text-lg md:text-xl italic text-[#983B50] mb-2 leading-[1.5rem] md:leading-[2.5rem] font-raleway">
              {t("wheatBagPage.title")}
            </p>
            <h1 className="text-xl md:text-3xl font-bold leading-[1.5rem] md:leading-[2.5rem] text-[#c48173] mb-4 font-raleway md:w-[27rem] mx-auto">
              {t("wheatBagPage.subtitle")}
            </h1>
            <p className="text-base md:text-xl text-[#983B50] leading-[1.5rem] md:leading-[2.5rem] font-raleway mb-4">
              {t("wheatBagPage.subsubtitle")}
            </p>

            {/* Sewing machine image */}
            <div className="flex justify-center items-center w-full mb-6">
              <img
                src="/images/sewingMachine.png"
                alt="Sewing machine"
                className="w-28 h-28 md:w-32 md:h-32 object-contain"
              />
            </div>

            <button
              onClick={scrollToShopCollection}
              className="bg-[#8A413B] text-white font-light py-2 px-6 rounded-[5px] shadow-lg hover:bg-[#662d2d] transition-colors"
            >
              {t("wheatBagPage.buttionText")}
            </button>
          </div>
        </div>

        {/* Right Column */}
        <div className="md:pt-[0rem] pt-[5rem] bg-[#a36161] md:bg-white order-1 md:order-none w-full md:w-[65%] bg-pink-100 flex justify-center items-center relative min-h-[100px] md:min-h-[100%]">
          <video
            ref={videoRef}
            src="/images/WheatBagVideo.mp4"
            autoPlay={false}
            loop
            muted={isMuted}
            className="w-full h-full object-fill md:object-cover"
          >
            Your browser does not support the video tag.
          </video>

          {/* Controls */}
          <div className="absolute bottom-4 right-4 flex space-x-2 z-10">
            <button
              onClick={toggleMute}
              className="bg-white/30 text-white p-2 rounded-full backdrop-blur-sm transition-colors hover:bg-white/50 focus:outline-none"
            >
              {isMuted ? "🔇" : "🔊"}
            </button>

            <button
              onClick={toggleFullscreen}
              className="bg-white/30 text-white p-2 rounded-full backdrop-blur-sm transition-colors hover:bg-white/50 focus:outline-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 0h-4m4 0l-5-5"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div id="wheatbag-shop-collection">
        <Product pageKey="wheatbag" />
      </div>
      <AdditionalSections />
      <WheatBagIntro />
      <WheatBagFaqs />
    </div>
  );
}

export default WheatBag;
